import React, { Suspense, useState, useEffect, useContext } from "react";
import { BrowserRouter as Router, Navigate, Outlet, Route, Routes } from "react-router-dom";

//-----------------------Components--------------------------
import MainNavigation from "./shared/components/Navigation/MainNavigation";
import Modal from "./shared/components/UIElements/Modal";
import ReleaseNotesModal from "./shared/components/UIElements/ReleaseNotesModal";
import { getUnseenReleaseNotes, latestReleaseVersion } from "./releaseNotes";
import { isSectionDisabled, isPageDisabled } from "./shared/utils/projectSections";
import Button from "./shared/components/FormElements/Button";

//----------------------Context--------------------------------
import { AuthContext } from "./shared/context/auth-context";
import { UnitPreferencesContext } from "./shared/context/unit-preferences-context";
import { ScenariosProvider } from "./shared/context/scenarios-context";

//-----------------------Hooks-------------------------------
import { UserAuth } from "./shared/hooks/auth-hook";
import { useUserProfile } from "./shared/hooks/useUserProfile";
import useProjectPermission from "./shared/hooks/useProjectPermission";
import { useProjectState } from "./shared/hooks/useProjectState";
import { useUnitPreferences } from "./shared/hooks/useUnitPreferences";
import { AssistantViewProvider } from "./assistant/context/AssistantViewContext";

//------------------------Pages-------------------------------
import HomePage from "./landing/pages/HomePage";
import BootScreen from "./shared/components/UIElements/BootScreen";

//-------------------------Code Splitting-------------------
const AuthPage = React.lazy(() => import("./account/auth/pages/AuthPage"));
const AuthCallbackPage = React.lazy(() => import("./account/auth/pages/AuthCallbackPage"));
const ResetPasswordPage = React.lazy(() => import("./account/auth/pages/ResetPasswordPage"));
const VerifyEmailPage = React.lazy(() => import("./account/auth/pages/VerifyEmailPage"));
const InvitePage = React.lazy(() => import("./account/auth/pages/InvitePage"));
const TermsPage = React.lazy(() => import("./legal/pages/TermsPage"));
const ProjectsPage = React.lazy(() => import("./account/projects/pages/ProjectsPage"));
const MyTasksPage = React.lazy(() => import("./account/tasks/pages/MyTasksPage"));
const ClientsPage = React.lazy(() => import("./account/company/pages/ClientsPage"));
const CompanyDetailsPage = React.lazy(() => import("./account/company/pages/CompanyDetailsPage"));
const ProjectDetailsPage = React.lazy(() => import("./account/projects/pages/ProjectDetailsPage"));
const UserSettingsPage = React.lazy(() => import("./account/settings/pages/UserSettingsPage"));
const SimulationConfigPage = React.lazy(() => import("./simulation/config/pages/SimulationConfigPage"));
const HeapsPage = React.lazy(() => import("./simulation/heaps/pages/HeapsPage"));
const KineticsPage = React.lazy(() => import("./simulation/kinetics/pages/KineticsPage"));
const KineticsFittingPage = React.lazy(() => import("./simulation/kinetics/pages/KineticsFittingPage"));
const LabTestsPage = React.lazy(() => import("./simulation/kinetics/pages/LabTestsPage"));
const HydrodynamicsPage = React.lazy(() => import("./simulation/hydrodynamics/pages/HydrodynamicsPage"));
const LeachingPage = React.lazy(() => import("./simulation/leaching/pages/LeachingPage"));
const WaterBalancePage = React.lazy(() => import("./simulation/climate/pages/WaterBalancePage"));
const ClimatePage = React.lazy(() => import("./simulation/climate/pages/ClimatePage"));
const RunPage = React.lazy(() => import("./simulation/run/pages/RunPage"));
const ResultsPage = React.lazy(() => import("./simulation/results/pages/ResultsPage"));
const LogsPage = React.lazy(() => import("./simulation/logs/pages/LogsPage"));
const ProjectManagementPage = React.lazy(() => import("./simulation/management/pages/ProjectManagementPage"));
const TaskTemplatesPage = React.lazy(() => import("./simulation/management/pages/TaskTemplatesPage"));
const HeapEditorPage = React.lazy(() => import("./simulation/heaps/pages/HeapEditorPage"));
const HeapComparePage = React.lazy(() => import("./simulation/heaps/pages/HeapComparePage"));
const NpvPage = React.lazy(() => import("./simulation/economics/pages/NpvPage"));
const EnsemblesPage = React.lazy(() => import("./simulation/ensembles/pages/EnsemblesPage"));
const ClosurePage = React.lazy(() => import("./simulation/closure/pages/ClosurePage"));
const ReconciliationPage = React.lazy(() => import("./simulation/reconciliation/pages/ReconciliationPage"));
const AsBuiltPage = React.lazy(() => import("./simulation/reconciliation/pages/AsBuiltPage"));
const OperationalDataPage = React.lazy(() => import("./operations/operationalData/pages/OperationalDataPage"));
const MaintenancePage = React.lazy(() => import("./operations/maintenance/pages/MaintenancePage"));
const DocumentRegisterPage = React.lazy(() => import("./compliance/documents/pages/DocumentRegisterPage"));
const DocumentSettingsPage = React.lazy(() => import("./compliance/documents/pages/DocumentSettingsPage"));
const DocumentDetailPage = React.lazy(() => import("./compliance/documents/pages/DocumentDetailPage"));
const TransmittalsPage = React.lazy(() => import("./compliance/documents/pages/TransmittalsPage"));
const TransmittalDetailPage = React.lazy(() => import("./compliance/documents/pages/TransmittalDetailPage"));
const MyApprovalsPage = React.lazy(() => import("./compliance/documents/pages/MyApprovalsPage"));
const SnapshotViewerPage = React.lazy(() => import('./simulation/results/pages/SnapshotViewerPage'));
const AdminCompaniesPage = React.lazy(() => import("./admin/pages/AdminCompaniesPage"));
const AdminUsersPage = React.lazy(() => import("./admin/pages/AdminUsersPage"));
const AdminProjectsPage = React.lazy(() => import("./admin/pages/AdminProjectsPage"));
const AdminBillingPage = React.lazy(() => import("./admin/pages/AdminBillingPage"));
const AdminSimulationsPage = React.lazy(() => import("./admin/pages/AdminSimulationsPage"));
const AdminSupportPage = React.lazy(() => import("./admin/pages/AdminSupportPage"));
const AdminBadgesPage = React.lazy(() => import("./admin/pages/AdminBadgesPage"));
const HelpPage = React.lazy(() => import("./help/pages/HelpPage"));
const HelpContactPage = React.lazy(() => import("./help/pages/HelpContactPage"));

/**
 * Route guard for /admin/*. The admin console shell used to render for any
 * authenticated user — no data leaked (Maestro 403s every admin endpoint) but
 * the whole console was reachable by URL.
 *
 * "Admin" is deliberately the same notion used everywhere else in the client
 * (useProjectPermission: role === 'admin' || permissions includes 'admin'),
 * which mirrors Maestro's requirePermissions bypass. This is a UX guard only —
 * the backend remains the enforcement point.
 *
 * Renders inside <AuthContext.Provider>, so the hook sees the live context.
 */
const RequireAdmin = ({ children }) => {
  const auth = useContext(AuthContext);
  const { isAdmin } = useProjectPermission();

  // Profile still loading (token restored before /auth/profile resolves, or a
  // re-fetch in useUserProfile): hold the route. Rendering children would flash
  // the admin shell at non-admins; redirecting would bounce a genuine admin who
  // deep-linked to /admin/users.
  if (!auth?.user) return <BootScreen />;

  return isAdmin ? children : <Navigate to="/projects" replace />;
};

/**
 * Layout-route guard for per-project section toggles (Project.disabledSections,
 * admin-managed via the Edit Project modal). Hiding the sidebar links is the
 * primary UX; this stops deep links and stale bookmarks into a section that has
 * been turned off for the project. UX guard only — the backend keeps enforcing
 * data access. Applies to admins too, so a disabled section behaves the same
 * for everyone.
 *
 * Renders inside <AuthContext.Provider>, so the hook sees the live context.
 */
const RequireSection = ({ section }) => {
  const auth = useContext(AuthContext);
  const disabled = isSectionDisabled(auth?.selectedProject, section);
  return disabled ? <Navigate to="/project-details" replace /> : <Outlet />;
};

/**
 * Page-level companion to RequireSection: guards a single page key
 * ('analysis.ensembles') so an admin can turn off one page while its section
 * stays available. isPageDisabled also covers the parent section being off,
 * so nesting under a RequireSection is belt-and-braces, not required.
 */
const RequirePage = ({ page }) => {
  const auth = useContext(AuthContext);
  const disabled = isPageDisabled(auth?.selectedProject, page);
  return disabled ? <Navigate to="/project-details" replace /> : <Outlet />;
};

const App = () => {
  const { token, login, logout, UID, user, authReady, showInactivityWarning, countdownSeconds, resetInactivityTimer, refreshSession } = UserAuth();
  const { selectedProject, setSelectedProject, selectedScenario, setSelectedScenario } = useProjectState();
  const { unitPreferences, updateUnitPreferences, saveUnitPreferences, isSaving: savingPreferences, loadError: prefLoadError } = useUnitPreferences(token, UID);
  const [sidebarPinned, setSidebarPinned] = useState(true); // Match sidebar's default pinned state
  const [adminSidebarPinned, setAdminSidebarPinned] = useState(false);
  const [showReleaseNotes, setShowReleaseNotes] = useState(false);
  const [releaseNotesEntries, setReleaseNotesEntries] = useState([]);

  // Fetch and sync user profile data (including profile photo)
  useUserProfile(token, UID, user, login);

  // Listen for project sidebar pin state changes
  useEffect(() => {
    const handleSidebarPinChange = (event) => {
      setSidebarPinned(event.detail.isPinned);
    };
    window.addEventListener('sidebarPinChanged', handleSidebarPinChange);
    return () => window.removeEventListener('sidebarPinChanged', handleSidebarPinChange);
  }, []);

  // Listen for admin sidebar pin state changes
  useEffect(() => {
    const handleAdminPinChange = (event) => {
      setAdminSidebarPinned(event.detail.isPinned);
    };
    window.addEventListener('adminSidebarPinChanged', handleAdminPinChange);
    return () => window.removeEventListener('adminSidebarPinChanged', handleAdminPinChange);
  }, []);

  // Mirror sidebar pin state onto <body> so overlays portaled outside <main>
  // (the shared Modal renders into #modal-hook) can center within the content
  // area rather than the window — see --pinned-sidebar-width in index.css.
  useEffect(() => {
    document.body.classList.toggle('has-pinned-sidebar', !!(token && selectedProject && sidebarPinned));
    document.body.classList.toggle('has-pinned-admin-sidebar', adminSidebarPinned);
  }, [token, selectedProject, sidebarPinned, adminSidebarPinned]);

  // Show "What's New" on every sign-in until the user acknowledges the current
  // release. Unseen entries (everything newer than the acknowledged version in
  // localStorage) come from releaseNotes.js — the changelog file itself, not an
  // env var, so this behaves the same in production builds.
  useEffect(() => {
    if (!token) return;
    const unseen = getUnseenReleaseNotes(localStorage.getItem("lastSeenVersion"));
    if (unseen.length > 0) {
      setReleaseNotesEntries(unseen);
      setShowReleaseNotes(true);
    }
  }, [token]);

  // Plain close ("Remind me later" / backdrop) — nothing persisted, so the
  // modal returns next sign-in. Only the acknowledge button records the version.
  const handleCloseReleaseNotes = () => setShowReleaseNotes(false);

  const handleAcknowledgeReleaseNotes = () => {
    if (latestReleaseVersion) {
      localStorage.setItem("lastSeenVersion", latestReleaseVersion);
    }
    setShowReleaseNotes(false);
  };

  // Terms of Use & attributions — public, and hoisted above the auth branch so
  // it resolves in every state: signed out (where "*" renders AuthPage), signed
  // in without a project, and signed in with one.
  const termsRoute = <Route path="/terms" element={<TermsPage />} />;

  let routes;
  if (token) {
    // Admin routes — accessible regardless of project selection, but gated on
    // the admin role by RequireAdmin (non-admins are redirected to /projects).
    const adminRoutes = (
      <>
        <Route path="/admin/companies" element={<RequireAdmin><AdminCompaniesPage /></RequireAdmin>} />
        <Route path="/admin/users" element={<RequireAdmin><AdminUsersPage /></RequireAdmin>} />
        <Route path="/admin/projects" element={<RequireAdmin><AdminProjectsPage /></RequireAdmin>} />
        <Route path="/admin/billing" element={<RequireAdmin><AdminBillingPage /></RequireAdmin>} />
        <Route path="/admin/simulations" element={<RequireAdmin><AdminSimulationsPage /></RequireAdmin>} />
        <Route path="/admin/support" element={<RequireAdmin><AdminSupportPage /></RequireAdmin>} />
        <Route path="/admin/badges" element={<RequireAdmin><AdminBadgesPage /></RequireAdmin>} />
      </>
    );

    // An invite link can land on an already-signed-in session — keep it
    // reachable in both branches so it accepts instead of falling through
    // to the catch-all "*" route.
    const inviteRoute = <Route path="/auth/invite" element={<InvitePage />} />;

    // My Tasks — cross-project, available with or without a project selected.
    const myTasksRoute = <Route path="/tasks" element={<MyTasksPage />} />;

    // Help Center — available with or without a project selected.
    const helpRoutes = (
      <>
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/contact" element={<HelpContactPage />} />
        <Route path="/help/:chapterId" element={<HelpPage />} />
      </>
    );

    if (selectedProject) {
      // When a project is selected, show simulation navigation
      routes = (
        <Routes>
          {adminRoutes}
          {inviteRoute}
          {helpRoutes}
          {termsRoute}
          {myTasksRoute}
          <Route path="*" element={<ProjectsPage setSelectedProject={setSelectedProject} />} />
          <Route path="/" element={<ProjectsPage setSelectedProject={setSelectedProject} />} />
          <Route path="/projects" element={<ProjectsPage setSelectedProject={setSelectedProject} />} />
          <Route path="/project-details" element={<ProjectDetailsPage />} />
          <Route path="/user-settings" element={<UserSettingsPage />} />
          {/* Non-sidebar routes ride with the page they're reached from:
              simulation-config + logs with Run, heap-editor with Heaps,
              snapshot-viewer with Results, detail pages with their register. */}
          <Route element={<RequireSection section="engineering" />}>
            <Route element={<RequirePage page="engineering.heaps" />}>
              <Route path="/heaps" element={<HeapsPage />} />
              <Route path="/heap-editor" element={<HeapEditorPage />} />
              <Route path="/heap-editor/:heapId" element={<HeapEditorPage />} />
              <Route path="/heap-compare/:heapId" element={<HeapComparePage />} />
            </Route>
            <Route element={<RequirePage page="engineering.kinetics" />}>
              <Route path="/kinetics" element={<KineticsPage />} />
            </Route>
            <Route element={<RequirePage page="engineering.hydrodynamics" />}>
              <Route path="/hydrodynamics" element={<HydrodynamicsPage />} />
            </Route>
            <Route element={<RequirePage page="engineering.leaching" />}>
              <Route path="/leaching" element={<LeachingPage />} />
            </Route>
            <Route element={<RequirePage page="engineering.run" />}>
              {/* Geode model editor carries its own page key on top of Run:
                  new projects start with it off, so a project opts in before
                  custom models are authored or picked on the Run page. */}
              <Route element={<RequirePage page="engineering.geode" />}>
                <Route path="/simulation-config" element={<SimulationConfigPage />} />
              </Route>
              <Route path="/run" element={<RunPage />} />
              <Route path="/logs" element={<LogsPage />} />
            </Route>
            <Route element={<RequirePage page="engineering.results" />}>
              <Route path="/results" element={<ResultsPage />} />
              <Route path="/snapshot-viewer" element={<SnapshotViewerPage />} />
            </Route>
          </Route>
          {/* Site-scoped inputs (split out of engineering; isSectionDisabled
              keeps them hidden while engineering itself is disabled) */}
          <Route element={<RequireSection section="kinetics" />}>
            <Route element={<RequirePage page="kinetics.labTests" />}>
              <Route path="/lab-tests" element={<LabTestsPage />} />
            </Route>
            <Route element={<RequirePage page="kinetics.fitting" />}>
              <Route path="/kinetics-fitting" element={<KineticsFittingPage />} />
            </Route>
          </Route>
          <Route element={<RequireSection section="sitewater" />}>
            <Route element={<RequirePage page="sitewater.waterBalance" />}>
              <Route path="/waterbalance" element={<WaterBalancePage />} />
            </Route>
            <Route element={<RequirePage page="sitewater.climate" />}>
              <Route path="/climate" element={<ClimatePage />} />
            </Route>
          </Route>
          <Route element={<RequireSection section="analysis" />}>
            <Route element={<RequirePage page="analysis.npv" />}>
              <Route path="/analysis/npv" element={<NpvPage />} />
            </Route>
            <Route element={<RequirePage page="analysis.ensembles" />}>
              <Route path="/analysis/ensembles" element={<EnsemblesPage />} />
            </Route>
            <Route element={<RequirePage page="analysis.closure" />}>
              <Route path="/analysis/closure" element={<ClosurePage />} />
            </Route>
            <Route element={<RequirePage page="analysis.reconciliation" />}>
              <Route path="/analysis/reconciliation" element={<ReconciliationPage />} />
              <Route path="/analysis/reconciliation/as-built" element={<AsBuiltPage />} />
            </Route>
          </Route>
          <Route element={<RequireSection section="management" />}>
            <Route element={<RequirePage page="management.tasks" />}>
              <Route path="/management" element={<ProjectManagementPage />} />
            </Route>
            <Route element={<RequirePage page="management.templates" />}>
              <Route path="/task-templates" element={<TaskTemplatesPage />} />
            </Route>
          </Route>
          <Route element={<RequireSection section="operation" />}>
            <Route element={<RequirePage page="operation.operationalData" />}>
              <Route path="/operational-data" element={<OperationalDataPage />} />
            </Route>
            <Route element={<RequirePage page="operation.maintenance" />}>
              <Route path="/maintenance" element={<MaintenancePage />} />
              <Route path="/maintenance/asset/:assetId" element={<MaintenancePage />} />
            </Route>
          </Route>
          <Route element={<RequireSection section="compliance" />}>
            <Route element={<RequirePage page="compliance.documents" />}>
              <Route path="/documents" element={<DocumentRegisterPage />} />
              <Route path="/documents/settings" element={<DocumentSettingsPage />} />
              <Route path="/documents/:documentId" element={<DocumentDetailPage />} />
            </Route>
            <Route element={<RequirePage page="compliance.transmittals" />}>
              <Route path="/transmittals" element={<TransmittalsPage />} />
              <Route path="/transmittals/:transmittalId" element={<TransmittalDetailPage />} />
            </Route>
            <Route element={<RequirePage page="compliance.approvals" />}>
              <Route path="/approvals" element={<MyApprovalsPage />} />
            </Route>
          </Route>
        </Routes>
      );
    } else {
      // When logged in but no project selected, show projects page
      routes = (
        <Routes>
          {adminRoutes}
          {inviteRoute}
          {helpRoutes}
          {termsRoute}
          {myTasksRoute}
          <Route path="*" element={<ProjectsPage setSelectedProject={setSelectedProject} />} />
          <Route path="/" element={<ProjectsPage setSelectedProject={setSelectedProject} />} />
          <Route path="/projects" element={<ProjectsPage setSelectedProject={setSelectedProject} />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/:companyId" element={<CompanyDetailsPage />} />
          <Route path="/user-settings" element={<UserSettingsPage />} />
        </Routes>
      );
    }
  } else {
    // If user not logged in, show home page and auth
    routes = (
      <Routes>
        <Route path="*" element={<AuthPage />} />
        {termsRoute}
        <Route path="/" element={<HomePage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/invite" element={<InvitePage />} />
      </Routes>
    );
  }

  // Gate the route tree until the mount-time session restore settles —
  // prevents an AuthPage flash and keeps /auth/callback from being swallowed
  // by the "*" route during an F5 mid-callback.
  if (!authReady) {
    routes = <BootScreen />;
  }

  return (
    <UnitPreferencesContext.Provider
      value={{ unitPreferences, updateUnitPreferences, saveUnitPreferences, isSaving: savingPreferences, loadError: prefLoadError }}
    >
    <AuthContext.Provider
      value={{
        isLoggedIn: !!token,
        token,
        UID,
        user,
        login,
        logout,
        selectedProject,
        setSelectedProject,
        selectedScenario,
        setSelectedScenario,
      }}
    >
      <ScenariosProvider>
        <AssistantViewProvider>
        <Router>
          {/* Inactivity warning — shown 5 minutes before the session expires */}
          <Modal
            show={showInactivityWarning}
            onCancel={refreshSession}
            header="Session Expiring Soon"
            footer={
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <Button inverse onClick={logout}>Log Out</Button>
                <Button onClick={refreshSession} disabled={countdownSeconds <= 0}>Continue Session</Button>
              </div>
            }
          >
            <p>Your session will expire due to inactivity.</p>
            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              Click <strong>Continue Session</strong> to stay logged in, or you will be logged out automatically.
            </p>
            <p style={{
              marginTop: '1rem',
              fontSize: '1.5rem',
              fontWeight: 700,
              textAlign: 'center',
              color: countdownSeconds <= 30 ? 'var(--error)' : countdownSeconds <= 60 ? 'var(--warning)' : 'var(--text-primary)',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '0.05em',
            }}>
              {Math.floor(countdownSeconds / 60)}:{String(countdownSeconds % 60).padStart(2, '0')}
            </p>
          </Modal>
          {/* Release notes — reappears each sign-in until acknowledged */}
          <ReleaseNotesModal
            show={showReleaseNotes}
            entries={releaseNotesEntries}
            onClose={handleCloseReleaseNotes}
            onAcknowledge={handleAcknowledgeReleaseNotes}
          />
          <MainNavigation />
          <main
            id="content"
            className={[
              'main-content',
              (token && selectedProject) ? 'with-sidebar' : '',
              (token && selectedProject && sidebarPinned) ? 'sidebar-pinned' : '',
              adminSidebarPinned ? 'admin-sidebar-pinned' : '',
            ].filter(Boolean).join(' ')}
          >
            <Suspense fallback={null}>
              {routes}
            </Suspense>
          </main>
        </Router>
        </AssistantViewProvider>
      </ScenariosProvider>
    </AuthContext.Provider>
    </UnitPreferencesContext.Provider>
  );
};

export default App;
